<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  isPdfDocument,
  formatDocumentLink,
  hasViewableFile,
} from 'shared/helpers/documentHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
  externalLink: {
    type: String,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  displayUrl: {
    type: String,
    default: '',
  },
  content: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['action', 'select']);
const { checkPermissions } = usePolicy();

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const menuItems = computed(() => {
  const allOptions = [];

  // Only when there is something a browser can open: an imported PDF has no
  // stored file, so its display_url is just the "PDF: name" label.
  if (hasViewableFile(props.displayUrl)) {
    allOptions.push({
      label: t('CAPTAIN.DOCUMENTS.OPTIONS.VIEW_FILE'),
      value: 'viewFile',
      action: 'viewFile',
      icon: 'i-ph-file-arrow-up',
    });
  }

  // The extracted text is what the assistant actually searches, so being able
  // to read it is how an operator tells "extraction worked" from "the PDF is
  // in the list but the assistant cannot see a word of it".
  if (props.content) {
    allOptions.push({
      label: t('CAPTAIN.DOCUMENTS.OPTIONS.VIEW_CONTENT'),
      value: 'viewContent',
      action: 'viewContent',
      icon: 'i-ph-article',
    });
  }

  allOptions.push({
    label: t('CAPTAIN.DOCUMENTS.OPTIONS.VIEW_RELATED_RESPONSES'),
    value: 'viewRelatedQuestions',
    action: 'viewRelatedQuestions',
    icon: 'i-ph-tree-view-duotone',
  });

  if (checkPermissions(['administrator'])) {
    // PDFs are uploaded for FAQ generation but their text does not always
    // reach the database, which hides them from the lexical search.
    if (isPdfDocument(props.externalLink)) {
      allOptions.push({
        label: t('CAPTAIN.DOCUMENTS.OPTIONS.EXTRACT_CONTENT'),
        value: 'extractContent',
        action: 'extractContent',
        icon: 'i-ph-text-align-left',
      });
    }

    allOptions.push({
      label: t('CAPTAIN.DOCUMENTS.OPTIONS.DELETE_DOCUMENT'),
      value: 'delete',
      action: 'delete',
      icon: 'i-lucide-trash',
    });
  }

  return allOptions;
});

const createdAt = computed(() => dynamicTime(props.createdAt));

const displayLink = computed(() => formatDocumentLink(props.externalLink));
const linkIcon = computed(() =>
  isPdfDocument(props.externalLink) ? 'i-ph-file-pdf' : 'i-ph-link-simple'
);

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};
</script>

<template>
  <CardLayout>
    <div class="flex gap-1 justify-between w-full">
      <span class="flex gap-2 items-center min-w-0">
        <Checkbox
          v-if="selectable"
          :model-value="selected"
          class="shrink-0"
          @change="emit('select', { id, selected: $event.target.checked })"
        />
        <span class="text-base text-n-slate-12 line-clamp-1">
          {{ name }}
        </span>
      </span>
      <div class="flex gap-2 items-center">
        <div
          v-on-clickaway="() => toggleDropdown(false)"
          class="flex relative items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="top-full mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0"
            @action="handleAction($event)"
          />
        </div>
      </div>
    </div>
    <div class="flex gap-4 justify-between items-center w-full">
      <span
        class="flex gap-1 items-center text-sm truncate shrink-0 text-n-slate-11"
      >
        <i class="i-woot-captain" />
        {{ assistant?.name || '' }}
      </span>
      <span
        class="flex flex-1 gap-1 justify-start items-center text-sm truncate text-n-slate-11"
      >
        <i :class="linkIcon" class="shrink-0" />
        <span class="truncate">{{ displayLink }}</span>
      </span>
      <div class="text-sm shrink-0 text-n-slate-11 line-clamp-1">
        {{ createdAt }}
      </div>
    </div>
  </CardLayout>
</template>
